import { computed, onMounted, watch, nextTick } from 'vue'
import he from 'he'

const clone = (value) => {
  if (value === null || value === undefined) {
    return value
  }

  // For arrays, handle them directly to avoid structuredClone issues
  if (Array.isArray(value)) {
    return [...value]
  }

  if (typeof value !== 'object') {
    return value
  }

  // For objects, use spread operator for a shallow clone
  try {
    return { ...value }
  } catch (e) {
    console.warn('Failed to clone object:', e)
    return {}
  }
}

const isEmpty = (v) => {
  return v === '' || v === null || v === undefined
}

const isArray = (v) => {
  return Array.isArray(v)
}

const isNumber = (v) => {
  return typeof v === 'number' && !isNaN(v)
}

const isBoolean = (v) => {
  return typeof v === 'boolean'
}

const isObject = (v) => {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

const castArray = (v) => {
  if (isArray(v)) {
    return v
  }

  if (typeof v === 'string') {
    const trimmed = v.trim()

    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (isArray(parsed)) {
          return parsed
        }
      } catch (e) {
        // If JSON parsing fails, fall through to comma-separated handling
      }
    }

    if (trimmed.includes(',')) {
      return trimmed.split(',').map(s => s.trim()).filter(s => s !== '')
    }

    return trimmed ? [trimmed] : []
  }

  if (v === null || v === undefined) {
    return []
  }

  return [v]
}

const castNumber = (v) => {
  if (isNumber(v)) {
    return v
  }

  if (typeof v === 'string') {
    const parsed = parseFloat(v)
    return isNaN(parsed) ? 0 : parsed
  }

  return 0
}

const castBoolean = (v) => {
  if (isBoolean(v)) {
    return v
  }

  if (typeof v === 'string') {
    const lowered = v.toLowerCase()
    return lowered === 'true' || lowered === '1' || lowered === 'yes'
  }

  return Boolean(v)
}

const castObject = (v, fallback) => {
  if (isObject(v)) {
    return v
  }

  if (typeof v === 'string') {
    try {
      const decoded = he.decode(v)
      const parsed = JSON.parse(decoded)

      if (isObject(parsed)) {
        return parsed
      }
    } catch (e) {
      // If parsing fails, return the fallback
    }
  }

  return clone(fallback || {})
}

const cast = (rawValue, defaultValue) => {
  if (isEmpty(rawValue)) {
    return clone(defaultValue)
  }

  if (isArray(defaultValue)) {
    return castArray(rawValue)
  }

  if (isNumber(defaultValue)) {
    return castNumber(rawValue)
  }

  if (isBoolean(defaultValue)) {
    return castBoolean(rawValue)
  }

  if (isObject(defaultValue)) {
    return castObject(rawValue, defaultValue)
  }

  return rawValue
}

const makeComputed = (props, key, defaultValue) => {
  return computed({
    get() {
      return cast(props.node?.attrs?.[key], defaultValue)
    },
    set(value) {
      // Convert arrays to comma-separated strings for backward compatibility
      const valueToUpdate = Array.isArray(value) ? value.join(',') : value
      props.updateAttributes({ [key]: valueToUpdate })
    },
  })
}

const initialiseDefaults = (props, attributes) => {
  onMounted(() => {
    const updates = {}

    Object.entries(attributes).forEach(([key, defaultValue]) => {
      const raw = props.node?.attrs?.[key]

      if (isEmpty(raw)) {
        // Convert arrays to comma-separated strings for backward compatibility
        updates[key] = Array.isArray(defaultValue) ? defaultValue.join(',') : defaultValue
      }
    })

    if (Object.keys(updates).length > 0) {
      nextTick(() => {
        props.updateAttributes(updates)
      })
    }
  })
}

export default function useNodeProps(props, ignored = ['id']) {
  const defaults = props.node.type.defaultAttrs || {}
  const initial = { ...defaults, ...props.node.attrs }
  const attributesToInitialize = {}

  const computedProps = Object.keys(initial).reduce((carry, key) => {
    if (!ignored.includes(key)) {
      const defaultValue = defaults[key]
      carry[key] = makeComputed(props, key, defaultValue)

      const isDeep = isObject(defaultValue) || isArray(defaultValue)

      watch(carry[key], (newVal) => {
        // Convert arrays to comma-separated strings for backward compatibility
        const valueToUpdate = Array.isArray(newVal) ? newVal.join(',') : newVal
        props.updateAttributes({ [key]: valueToUpdate })
      }, {
        deep: isDeep,
        // Using flush: 'post' prevents update loops by ensuring changes are processed after the current tick
        flush: 'post'
      })

      attributesToInitialize[key] = defaultValue
    }

    return carry
  }, {})

  initialiseDefaults(props, attributesToInitialize)

  return computedProps
}
